import { z } from "zod"

type TranslateFn = (key: string) => any

export const generalSchema = (t: TranslateFn) =>
  z.object({
    projectName: z
      .string({
        required_error: t("Project name is required"),
      })
      .min(1, t("Project name is required")),
    // contractorName: z
    //   .number({
    //     required_error: "Contractor name is required",
    //     invalid_type_error: "Contractor name is required",
    //   })
    //   .min(1, "Contractor name is required"),
    // contractorName: z.number().optional(),
    company_id: z.number().optional(),
    number: z.string().optional(),
    siteAddress: z.string().optional(),
    date: z
      .string({ required_error: t("Date is required") })
      .refine((val) => !isNaN(Date.parse(val)), { message: t("Invalid date format") }),
    status: z.number().optional(),
    description: z.string().optional(),
    weather: z
      .object({
        description: z.number().optional(),
        temperature: z.union([z.string().transform((val) => Number(val)), z.number()]).optional(),
        rating: z.number().optional(),
      })
      .optional(),
    workDetails: z
      .array(
        z.object({
          id: z.number().optional(),
          actionType: z.enum(["add", "edit", "delete"]).optional(), // <== include this
          description: z
            .string({
              required_error: t("Item description is required"),
            })
            .min(1, t("Item description is required")),
          amount: z
            .number({
              required_error: t("Quantity is required"),
              invalid_type_error: t("Quantity must be a number"),
            })
            .min(0, t("Quantity must be positive")),
          units: z
            .string({
              required_error: t("Unit is required"),
            })
            .min(1, t("Unit is required")),
        }),
      )
      .optional(),
  })

export const resourcesSchema = (t: TranslateFn) =>
  z.object({
    siteResources: z.array(
      z.object({
        id: z.number().optional(),
        resource_id: z.number().optional(),
        actionType: z.enum(["add", "edit", "delete"]).optional(), // <== include this
        typeName: z.union([z.string().min(1, t("Resource name is required")), z.number()]),
        translatedName: z.string().optional(),
        type: z.string().min(1, t("Resource type is required")),
        quantity: z.number({ invalid_type_error: t("Quantity must be a number") }).min(0, t("Quantity must be positive")),
        remarks: z.string().optional(),
      }),
    ),
    workforce: z.array(
      z.object({
        id: z.number().optional(),
        resource_id: z.number().optional(),
        actionType: z.enum(["add", "edit", "delete"]).optional(), // <== include this
        typeName: z.union([z.string().min(1, t("Worker name is required")), z.number()]),
        translatedName: z.string().optional(),
        type: z.string().min(1, t("Worker type is required")),
        quantity: z.number({ invalid_type_error: t("Quantity must be a number") }).min(0, t("Quantity must be positive")),
        remarks: z.string().optional(),
      }),
    ),
    equipment: z.array(
      z.object({
        id: z.number().optional(),
        resource_id: z.number().optional(),
        actionType: z.enum(["add", "edit", "delete"]).optional(), // <== include this
        typeName: z.union([z.string().min(1, t("Equipment name is required")), z.number()]),
        translatedName: z.string().optional(),
        type: z.string().min(1, t("Equipment type is required")),
        quantity: z.number({ invalid_type_error: t("Quantity must be a number") }).min(0, t("Quantity must be positive")),
        remarks: z.string().optional(),
      }),
    ),
  })

export const tasksSchema = (t: TranslateFn) =>
  z.object({
    tasks: z.array(
      z.object({
        id: z.number().optional(),
        actionType: z.enum(["add", "edit", "delete"]).optional(), // <== include this
        description: z.string().min(1, t("Task description is required")),
        assignedTo: z.array(z.number()),
        completed: z.boolean().default(false),
      }),
    ),
  })

export const commentsSchema = z.object({
  comments: z.string().optional(),
  contractorApproval: z.boolean().default(false),
  supervisorApproval: z.boolean().default(false),
  images: z
    .array(z.instanceof(File)) // Accepts array of File objects
    .optional(),
})

export type GeneralFormData = z.infer<ReturnType<typeof generalSchema>>
export type ResourcesFormData = z.infer<ReturnType<typeof resourcesSchema>>
export type TasksFormData = z.infer<ReturnType<typeof tasksSchema>>
export type CommentsFormData = z.infer<typeof commentsSchema>

export const workLogSchema = (t: TranslateFn) =>
  generalSchema(t)
    .merge(resourcesSchema(t))
    .merge(tasksSchema(t))
    .merge(commentsSchema)
export type WorkLogFormData = z.infer<ReturnType<typeof workLogSchema>>
